import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { debounce } from 'lodash';
import { withRouter } from 'react-router-dom';
import { FiEdit, FiSettings } from 'react-icons/fi';
import styled from 'styled-components';

import { Button, AnchorButton, Callout, Flex, Text } from 'core/components';
import Page from 'app/components/page/Page';
import TestPauseResumeButton from 'app/views/synthetics/tests/TestPauseResumeButton';
import { hideTooltips, handleCopyTest } from 'app/views/synthetics/utils/syntheticsUtils';
import { TESTS_PAGE_PARENT_LINK } from '@kentik/ui-shared/synthetics/constants';
import TestConfigDetails from 'app/views/synthetics/components/TestConfigDetails';
import AgentDetails from 'app/views/synthetics/agents/AgentDetails';
import { SynthAgentDrawerProvider } from 'app/views/synthetics/components/agentDrawer/SynthAgentDrawer';
import TestResultsPageHeader from './TestResultsPageHeader';

const DrawerContent = styled.div`
  height: 100%;
`;

@inject('$auth', '$dataviews', '$sharedLinks', '$syn')
@observer
@withRouter
class TestResultsPageWrapper extends Component {
  state = { showConfigDrawer: false, openAgentId: null };

  handleToggleConfigDrawer = () => {
    this.setState((prev) => ({ showConfigDrawer: !prev.showConfigDrawer, openAgentId: null }), this.triggerResize);
  };

  handleCloseConfigDrawer = () => {
    this.setState({ showConfigDrawer: false }, this.triggerResize);
  };

  // Agent details takes over the page drawer; closing it reveals whatever was open before.
  handleOpenAgent = (openAgentId) => {
    this.setState({ openAgentId }, this.triggerResize);
  };

  handleCloseAgent = () => {
    this.setState({ openAgentId: null }, this.triggerResize);
  };

  triggerResize = () => {
    // Dispatch after drawer transition (150ms) so charts recalculate dimensions
    setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
  };

  handleOnScroll = debounce(() => {
    const { $dataviews } = this.props;

    hideTooltips($dataviews);
  }, 50);

  get title() {
    const { test } = this.props;

    if (test.isPreview) {
      return 'Test Preview';
    }

    return test.get('display_name') || 'Test Results';
  }

  get showExport() {
    const { test, exportUrl } = this.props;
    const { bigDataSet, displayOptions } = test.results;
    const { allowExport } = displayOptions;

    if (allowExport) {
      const options = { passLocation: false, location: exportUrl };

      if (bigDataSet) {
        options.sleepDuration = 30000;
      }

      return options;
    }

    return false;
  }

  get shareButtonProps() {
    const { $sharedLinks, test } = this.props;
    const { actualStartDate, actualEndDate, displayOptions } = test.results;
    const { allowShare } = displayOptions;

    if (allowShare) {
      // time selections for SharePublicViewDialog
      let privateShareUrl;
      if ($sharedLinks && $sharedLinks.history && $sharedLinks.history.location) {
        privateShareUrl = `${$sharedLinks.history?.location?.pathname}${$sharedLinks.history?.location?.search}`;
      }

      return {
        link_type: $sharedLinks.types.synthetics,
        test_id: test.id,
        start_time: actualStartDate,
        end_time: actualEndDate,
        legacyLinkShare: false,
        privateShareUrl
      };
    }

    return {};
  }

  get agentModel() {
    const { $syn } = this.props;
    const { openAgentId } = this.state;

    if (openAgentId == null || !$syn.agents) {
      return null;
    }

    return $syn.agents.get(openAgentId) || null;
  }

  render() {
    const { $auth, history, test, children, match } = this.props;
    const { showConfigDrawer } = this.state;
    const { agentModel } = this;
    const parentLinks = [TESTS_PAGE_PARENT_LINK];
    const canEditTests = $auth.hasRbacPermissions(['synthetics.test::update']);
    const showTestActions = canEditTests && !test.isPreview;
    const subnavTools = [
      {
        id: 'pause-resume-test',
        model: test,
        component: TestPauseResumeButton,
        show: showTestActions && !test.isPreset
      },
      {
        id: 'customize-test',
        text: 'Customize this test',
        icon: FiEdit,
        onClick: (e) => {
          e.stopPropagation();
          handleCopyTest(history, test);
        },
        show: showTestActions && test.isPreset
      },
      {
        id: 'settings',
        text: 'Settings',
        icon: FiSettings,
        onClick: this.handleToggleConfigDrawer,
        show: true
      }
    ]
      .filter(({ show }) => show)
      .map(({ id, component, show, ...rest }) => {
        const Btn = component || Button;
        return <Btn id={id} key={id} {...rest} minimal />;
      });

    return (
      <Page
        title={this.title}
        parentLinks={parentLinks}
        subnavTools={subnavTools}
        scrolls
        overflow="auto scroll"
        canDockDrawer
        showExport={this.showExport}
        forceShowExport={!!this.showExport}
        shareButtonProps={this.shareButtonProps}
        onScroll={this.handleOnScroll}
        drawerContents={
          <DrawerContent>
            {agentModel ? (
              <AgentDetails
                model={agentModel}
                visibleTabs={['summary', 'tests']}
                hideActions
                drawerOnClose={this.handleCloseAgent}
              />
            ) : (
              <TestConfigDetails
                test={test}
                editUrl={showTestActions && !test.isPreset ? `/v4/synthetics/tests/${test.id}` : null}
                onClose={this.handleCloseConfigDrawer}
              />
            )}
          </DrawerContent>
        }
        drawerIsOpen={showConfigDrawer || !!agentModel}
        drawerOnClose={agentModel ? this.handleCloseAgent : this.handleCloseConfigDrawer}
        drawerProps={{
          showDockedInLargeFullScreen: false,
          showCloseButton: !!agentModel,
          style: { width: 480, minWidth: 480 }
        }}
      >
        <SynthAgentDrawerProvider onOpenAgent={this.handleOpenAgent}>
          {$auth.isSynOnboarding && (
            <Callout intent="primary" mb={4} px={2}>
              <Flex alignItems="center" justifyContent="space-between">
                <Text color="muted">
                  Modify test to gain deeper results based on your specific configuration and resolve identified issues.
                </Text>
                <Flex gap={2}>
                  <AnchorButton
                    minimal
                    rel="noopener noreferrer"
                    intent="warning"
                    fontSize="small"
                    fontWeight="bold"
                    target="_blank"
                    href="https://kb.kentik.com/docs/synthetics-overview"
                    text="Learn More"
                    style={{ textTransform: 'uppercase' }}
                  />
                  <Button
                    minimal
                    intent="warning"
                    fontSize="small"
                    fontWeight="bold"
                    style={{ textTransform: 'uppercase' }}
                    onClick={() => history.push(`/v4/synthetics/tests/${test.id}`)}
                  >
                    Modify Test
                  </Button>
                </Flex>
              </Flex>
            </Callout>
          )}
          <TestResultsPageHeader test={test} match={match} />
          {children}
        </SynthAgentDrawerProvider>
      </Page>
    );
  }
}

export default TestResultsPageWrapper;
